import {
  AlertTriangle,
  Check,
  Download,
  FileSpreadsheet,
  ShieldCheck,
  Upload,
  UserRound,
  UserRoundCheck,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import Flag from "react-world-flags";

import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

const STILL = "motion-reduce:animate-none";
const CARD = "rounded-xl border border-border bg-card shadow-xs";
const MICRO_LABEL = "font-mono text-[10px] uppercase tracking-wider text-muted-foreground";
const FLAG = "h-3 w-[18px] shrink-0 rounded-[2px] object-cover";
const ROW_ENTER = ["animate-delay-150", "animate-delay-400", "animate-delay-650", "animate-delay-900"];
const MARK_ENTER = ["animate-delay-500", "animate-delay-800", "animate-delay-1100", "animate-delay-1400"];

function useCop() {
  const { activeLanguage } = useTranslations("payoutBatches");
  return (amount: string) => formatCurrencyAmount(amount, "COP", activeLanguage);
}

function Bar({ className }: { className?: string }) {
  return <span className={cn("block h-2 rounded-full bg-muted-foreground/20", className)} />;
}

function Chip({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <p className={MICRO_LABEL}>{label}</p>
      <div className={cn(CARD, "flex items-center gap-2 px-3 py-2 text-sm")}>{children}</div>
    </div>
  );
}

export function RouteScene() {
  const { t } = useTranslations("payoutBatches");
  const countryLabel = useCountryLabel();

  return (
    <div className="flex w-full max-w-md items-end justify-center gap-2 sm:gap-3">
      <Chip label={t("setup_route_from")} className={cn("animate-fade-right animate-duration-500", STILL)}>
        <Flag code="CO" className={FLAG} aria-hidden="true" />
        <span className="font-semibold">COP</span>
      </Chip>
      <div className="relative mb-[18px] h-0.5 w-12 shrink-0 rounded-full bg-primary/25 sm:w-16">
        <span
          className={cn(
            "absolute -top-1 right-0 size-2.5 rounded-full bg-primary",
            "animate-fade-right animate-duration-1000 animate-infinite animate-ease-in-out",
            STILL,
          )}
        />
      </div>
      <Chip
        label={t("setup_route_to")}
        className={cn("animate-fade-left animate-duration-500 animate-delay-300", STILL)}
      >
        <Flag code="CO" className={FLAG} aria-hidden="true" />
        <span className="font-semibold">{countryLabel("COL", "Colombia")}</span>
        <span className="text-muted-foreground">· {formatRail("BREB")}</span>
      </Chip>
    </div>
  );
}

export function TemplateScene() {
  const { t } = useTranslations("payoutBatches");
  const cop = useCop();
  const rows = [
    { name: "w-24", amount: "250000" },
    { name: "w-16", amount: "1200000" },
    { name: "w-20", amount: "80000" },
  ];

  return (
    <div className={cn(CARD, "w-full max-w-sm overflow-hidden")}>
      <div className="flex items-center gap-2 border-b border-border bg-muted/60 px-3 py-2">
        <FileSpreadsheet aria-hidden="true" className="size-4 text-primary" />
        <span className="font-mono text-[11px] text-muted-foreground">{t("guide_scene_file")}</span>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_7rem] text-xs">
        <span className={cn(MICRO_LABEL, "border-b border-border px-3 py-1.5")}>
          {t("items_col_beneficiary")}
        </span>
        <span
          className={cn(MICRO_LABEL, "border-b border-l border-border bg-primary/5 px-3 py-1.5 text-right")}
        >
          {t("items_col_receive")}
        </span>
        {rows.map((row, index) => (
          <div
            key={row.amount}
            className={cn(
              "col-span-2 grid grid-cols-subgrid animate-fade-down animate-duration-500",
              index < rows.length - 1 && "border-b border-border",
              ROW_ENTER[index],
              STILL,
            )}
          >
            <span className="flex items-center px-3 py-2">
              <Bar className={row.name} />
            </span>
            <span className="border-l border-border bg-primary/5 px-3 py-2 text-right font-mono font-semibold text-foreground tabular-nums">
              {cop(row.amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UploadScene() {
  const { t } = useTranslations("payoutBatches");
  const marks = ["ok", "ok", "warning", "ok"] as const;

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-3">
      <div
        className={cn(
          CARD,
          "flex items-center gap-2 px-3 py-1.5 text-xs animate-fade-up animate-duration-500",
          STILL,
        )}
      >
        <Upload aria-hidden="true" className="size-3.5 text-primary" />
        <span className="font-mono text-muted-foreground">{t("guide_scene_file")}</span>
        <ShieldCheck aria-hidden="true" className="size-3.5 text-success" />
      </div>
      <div className={cn(CARD, "w-full divide-y divide-border")}>
        {marks.map((mark, index) => (
          <div key={index} className="flex items-center gap-3 px-3 py-2">
            <Bar className={["w-28", "w-20", "w-24", "w-16"][index]} />
            <span
              className={cn(
                "ml-auto flex size-5 items-center justify-center rounded-full animate-jump-in animate-duration-300",
                mark === "ok" ? "bg-success/15 text-success" : "bg-warning/15 text-warning",
                MARK_ENTER[index],
                STILL,
              )}
            >
              {mark === "ok" ? (
                <Check aria-hidden="true" className="size-3" />
              ) : (
                <AlertTriangle aria-hidden="true" className="size-3" />
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReviewScene() {
  const { t } = useTranslations("payoutBatches");
  const cop = useCop();
  const rows = [
    { bar: "w-24", amount: "250000", selected: true },
    { bar: "w-16", amount: "1200000", selected: true },
    { bar: "w-20", amount: null, selected: false },
  ];

  return (
    <div className={cn(CARD, "w-full max-w-sm divide-y divide-border")}>
      {rows.map((row, index) => (
        <div key={index} className={cn("flex items-center gap-3 px-3 py-2", !row.selected && "opacity-60")}>
          <span className="relative flex size-4 shrink-0 rounded border border-border">
            {row.selected && (
              <span
                className={cn(
                  "absolute -inset-px flex items-center justify-center rounded bg-primary text-primary-foreground animate-jump-in animate-duration-300",
                  MARK_ENTER[index],
                  STILL,
                )}
              >
                <Check aria-hidden="true" className="size-3" />
              </span>
            )}
          </span>
          <Bar className={cn(row.bar, !row.selected && "bg-destructive/30")} />
          <span className="ml-auto font-mono text-xs tabular-nums">
            {row.amount ? (
              cop(row.amount)
            ) : (
              <AlertTriangle aria-hidden="true" className="size-3.5 text-destructive" />
            )}
          </span>
        </div>
      ))}
      <div
        className={cn(
          "flex items-center justify-between gap-3 rounded-b-xl bg-muted/50 px-3 py-2.5 animate-fade-up animate-duration-500 animate-delay-1100",
          STILL,
        )}
      >
        <span className="text-xs text-muted-foreground">{t("totals_debit")}</span>
        <span className="font-mono text-sm font-semibold tabular-nums">{cop("1454000")} COP</span>
      </div>
    </div>
  );
}

export function ApprovalScene() {
  const { t } = useTranslations("payoutBatches");

  return (
    <div className="flex w-full max-w-sm items-start justify-between gap-3">
      <div className="flex w-20 flex-col items-center gap-2">
        <span className="flex size-12 items-center justify-center rounded-full border border-border bg-card">
          <UserRound aria-hidden="true" className="size-5 text-muted-foreground" />
        </span>
        <span className="text-xs text-muted-foreground">{t("actor_you")}</span>
      </div>
      <div className="relative mt-3 flex flex-1 justify-center">
        <span
          className={cn(
            CARD,
            "flex items-center gap-1.5 px-2.5 py-1.5 animate-fade-right animate-duration-700 animate-delay-200",
            STILL,
          )}
        >
          <FileSpreadsheet aria-hidden="true" className="size-4 text-primary" />
          <Bar className="w-10" />
        </span>
      </div>
      <div className="flex w-20 flex-col items-center gap-2">
        <span className="relative flex size-12 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
          <UserRoundCheck aria-hidden="true" className="size-5 text-primary" />
          <span
            className={cn(
              "absolute -right-1 -bottom-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card animate-jump-in animate-duration-300 animate-delay-1000",
              STILL,
            )}
          >
            <ShieldCheck aria-hidden="true" className="size-3" />
          </span>
        </span>
        <span className="text-xs font-medium text-foreground">{t("guide_scene_approver")}</span>
      </div>
    </div>
  );
}

export function SelfApprovalScene() {
  const { t } = useTranslations("payoutBatches");

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-3">
      <span
        className={cn(
          CARD,
          "flex items-center gap-1.5 px-2.5 py-1.5 animate-fade-down animate-duration-700 animate-delay-200",
          STILL,
        )}
      >
        <FileSpreadsheet aria-hidden="true" className="size-4 text-primary" />
        <Bar className="w-10" />
      </span>
      <span className="relative flex size-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
        <UserRoundCheck aria-hidden="true" className="size-6 text-primary" />
        <span
          className={cn(
            "absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground ring-2 ring-card animate-jump-in animate-duration-300 animate-delay-1000",
            STILL,
          )}
        >
          <ShieldCheck aria-hidden="true" className="size-3.5" />
        </span>
      </span>
      <span className="text-xs font-medium text-foreground">{t("guide_scene_self_approver")}</span>
    </div>
  );
}

const RUN_OUTCOMES = [
  "paid",
  "paid",
  "paid",
  "failed",
  "paid",
  "paid",
  "paid",
  "paid",
  "paid",
  "paid",
] as const;
const RUN_TICK_MS = 260;

export function RunScene() {
  const { t } = useTranslations("payoutBatches");
  const reduced = usePrefersReducedMotion();
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setInterval(() => {
      setTick((current) => (current >= RUN_OUTCOMES.length ? current : current + 1));
    }, RUN_TICK_MS);
    return () => window.clearInterval(timer);
  }, [reduced]);

  const processed = reduced ? RUN_OUTCOMES.length : tick;
  const done = RUN_OUTCOMES.slice(0, processed);
  const paid = done.filter((outcome) => outcome === "paid").length;
  const failed = done.length - paid;
  const inFlight = processed < RUN_OUTCOMES.length ? 1 : 0;
  const finished = processed >= RUN_OUTCOMES.length;

  return (
    <div className={cn(CARD, "w-full max-w-sm space-y-3 p-4")}>
      <ColocaProgress
        value={processed}
        max={RUN_OUTCOMES.length}
        size="lg"
        aria-label={t("progress_title")}
        segments={[
          { key: "paid", value: paid, tone: "success" },
          { key: "failed", value: failed, tone: "danger" },
          { key: "processing", value: inFlight, tone: "primary" },
        ]}
      />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-success" />
          {t("progress_legend_paid")} <span className="font-mono text-foreground tabular-nums">{paid}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full bg-destructive" />
          {t("progress_legend_failed")}{" "}
          <span className="font-mono text-foreground tabular-nums">{failed}</span>
        </span>
      </div>
      <div className="flex h-7 items-center">
        {finished && (
          <span
            className={cn(
              "flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs font-medium animate-fade-up animate-duration-300",
              STILL,
            )}
          >
            <Download aria-hidden="true" className="size-3.5 text-primary" />
            {t("action_download_report")}
          </span>
        )}
      </div>
    </div>
  );
}
